import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ClientBackground from "../components/ClientBackground";
import api from "../api";
import LoadingSpinner from "../components/LoadingSpinner";
import { ChevronLeft, Heart, ChevronDown } from "lucide-react";

export default function RecommendedActivities() {
  const { childId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [childName, setChildName] = useState("");
  const [childAgeWeeks, setChildAgeWeeks] = useState(null);
  const [activities, setActivities] = useState([]);
  const [activeFilter, setActiveFilter] = useState("all");
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [favorites, setFavorites] = useState(new Set());
  const [reminders, setReminders] = useState(new Set());

  useEffect(() => {
    const fetchActivities = async () => {
      try {
        setLoading(true);
        const response = await api.get(
          `/children/recommended-activities/by-child/?child_id=${childId}`
        );

        const childResponse = await api.get(`/children/profiles/${childId}/`);
        setChildName(childResponse.data.first_name);

        if (childResponse.data.dob) {
          const dob = new Date(childResponse.data.dob);
          const now = new Date();
          const diffTime = Math.abs(now - dob);
          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
          const weeks = Math.floor(diffDays / 7) + 1;
          setChildAgeWeeks(weeks);
        }

        const activities = response.data;
        const questionSetsResponse = await api.get('/children/global-question-sets/');
        const questionSets = questionSetsResponse.data;

        const activitiesBySet = questionSets.map(qs => ({
          ...qs,
          activities: activities.filter(a => a.band_set === qs.id)
        }));

        setActivities(activitiesBySet);

        const favs = new Set();
        const rems = new Set();
        activities.forEach((activity) => {
          if (activity.status === 'favourite') favs.add(activity.id);
          if (activity.status === 'remind_me') rems.add(activity.id);
        });
        setFavorites(favs);
        setReminders(rems);
      } catch (err) {
        setError("Failed to load recommended activities.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    if (childId) {
      fetchActivities();
    }
  }, [childId]);

  const toggleFavorite = async (activityId) => {
    const newFavorites = new Set(favorites);
    if (newFavorites.has(activityId)) {
      newFavorites.delete(activityId);
    } else {
      newFavorites.add(activityId);
    }
    setFavorites(newFavorites);

    try {
      await api.post(`/children/recommended-activities/${activityId}/toggle-favorite/`, {
        child_id: childId,
      });
    } catch (err) {
      console.error("Failed to toggle favorite:", err);
      setFavorites(favorites);
    }
  };

  const toggleReminder = async (activityId) => {
    const newReminders = new Set(reminders);
    if (newReminders.has(activityId)) {
      newReminders.delete(activityId);
    } else {
      newReminders.add(activityId);
    }
    setReminders(newReminders);

    try {
      await api.post(`/children/recommended-activities/${activityId}/toggle-reminder/`, {
        child_id: childId,
      });
    } catch (err) {
      console.error("Failed to toggle reminder:", err);
      setReminders(reminders);
    }
  };

  const getCategoryColor = (categoryName) => {
    const name = categoryName?.toLowerCase() || "";
    if (name.includes("social") || name.includes("emotional")) return "bg-[#e0f2fe]";
    if (name.includes("language")) return "bg-[#fef3c7]";
    if (name.includes("motor")) return "bg-[#fce7f3]";
    if (name.includes("cognitive")) return "bg-[#dbeafe]";
    return "bg-[#f3f4f6]";
  };

  const filteredActivities = activities
    .map((qs) => ({
      ...qs,
      activities: qs.activities?.filter((activity) => {
        // Filter 1: Active Filter (All, Fav, Reminder)
        let matchesFilter = true;
        if (activeFilter === "favourite") matchesFilter = favorites.has(activity.id);
        if (activeFilter === "reminder") matchesFilter = reminders.has(activity.id);
        
        // Filter 2: Week Filter (Precision +/- 1 week)
        let matchesWeek = true;
        if (childAgeWeeks) {
          if (activity.week) {
             const activityWeek = parseInt(activity.week);
             matchesWeek = activityWeek >= childAgeWeeks - 1 && activityWeek <= childAgeWeeks + 1;
          } else {
             // If activity has no week assigned, hide it to ensure strict week matching
             matchesWeek = false; 
          }
        }

        return matchesFilter && matchesWeek;
      }),
    }))
    .filter((qs) => qs.activities && qs.activities.length > 0);

  const getFilterLabel = () => {
    if (activeFilter === "all") return "All";
    if (activeFilter === "favourite") return "Favourite";
    if (activeFilter === "reminder") return "Reminder";
    return "All";
  };

  if (loading) {
    return (
      <div className="relative min-h-screen">
        <ClientBackground />
        <div className="relative z-10 flex flex-col items-center justify-center min-h-screen">
          <LoadingSpinner />
          <p className="text-gray-500 mt-2">Loading activities...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="relative min-h-screen">
        <ClientBackground />
        <div className="relative z-10 flex flex-col items-center justify-center min-h-screen">
          <p className="text-red-500">{error}</p>
          <button
            onClick={() => navigate(-1)}
            className="mt-4 px-4 py-2 bg-[#1e3a5f] text-white rounded-lg"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen">
      <ClientBackground />

      <div className="relative z-10 flex flex-col min-h-screen">
        <div className="fixed top-0 left-0 right-0 z-50 bg-white/30 backdrop-blur-sm px-4 md:px-8 py-4 flex items-center justify-between shadow-sm">
          <button onClick={() => navigate(-1)} className="p-2 -ml-2">
            <ChevronLeft className="w-6 h-6 text-gray-800" />
          </button>
          <div className="flex flex-col items-center">
            <h1 className="text-lg md:text-xl font-semibold text-[#1e3a5f]">
              Recommended Activities
            </h1>
            <p className="text-xs text-gray-500">
              Activities for {childName}
            </p>
            {childAgeWeeks && (
              <span className="mt-1 px-3 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                Week {childAgeWeeks}
              </span>
            )}
          </div>
          <div className="w-8" />
        </div>

        <div className="flex-1 px-4 md:px-6 lg:px-8 mt-24 pb-8">
          <div className="max-w-5xl mx-auto">
            <div className="mb-6 flex justify-center">
              <div className="relative w-full md:w-64">
                <button
                  onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                  className="w-full px-4 py-3 bg-white/80 backdrop-blur-sm border border-[#d0d8e6] rounded-xl flex items-center justify-between text-[#1e3a5f] font-medium text-sm shadow-sm"
                >
                  <span>{getFilterLabel()}</span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${showFilterDropdown ? "rotate-180" : ""}`} />
                </button>

                {showFilterDropdown && (
                  <div className="absolute top-full mt-2 w-full bg-white border border-[#d0d8e6] rounded-xl shadow-lg overflow-hidden z-10">
                    <button
                      onClick={() => {
                        setActiveFilter("all");
                        setShowFilterDropdown(false);
                      }}
                      className={`w-full px-4 py-3 text-left text-sm hover:bg-gray-50 ${activeFilter === "all" ? "bg-[#1e3a5f]/5 text-[#1e3a5f] font-medium" : "text-gray-700"
                        }`}
                    >
                      All
                    </button>
                    <button
                      onClick={() => {
                        setActiveFilter("favourite");
                        setShowFilterDropdown(false);
                      }}
                      className={`w-full px-4 py-3 text-left text-sm hover:bg-gray-50 ${activeFilter === "favourite" ? "bg-[#1e3a5f]/5 text-[#1e3a5f] font-medium" : "text-gray-700"
                        }`}
                    >
                      Favourite
                    </button>
                    <button
                      onClick={() => {
                        setActiveFilter("reminder");
                        setShowFilterDropdown(false);
                      }}
                      className={`w-full px-4 py-3 text-left text-sm hover:bg-gray-50 ${activeFilter === "reminder" ? "bg-[#1e3a5f]/5 text-[#1e3a5f] font-medium" : "text-gray-700"
                        }`}
                    >
                      Reminder
                    </button>
                  </div>
                )}
              </div>
            </div>

            {filteredActivities.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500">No activities found.</p>
              </div>
            ) : (
              <div className="space-y-10">
                {filteredActivities.map((questionSet) => (
                  <div key={questionSet.id}>
                    <h2 className="text-lg md:text-xl font-semibold text-[#1e3a5f] mb-5">
                      {questionSet.title}
                    </h2>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {questionSet.activities.map((activity) => (
                        <div
                          key={activity.id}
                          className={`${getCategoryColor(
                            questionSet.title
                          )} rounded-3xl overflow-hidden shadow-sm`}
                        >
                          <div className="px-8 py-6 min-h-[140px]">
                            <div className="flex justify-between items-start mb-2">
                              {activity.week && (
                                <span className="inline-block px-3 py-1 bg-white/50 text-[#1e3a5f] text-sm font-semibold rounded-full mb-2">
                                  Week {activity.week}
                                </span>
                              )}
                            </div>
                            <p className="text-base md:text-lg text-[#1e3a5f] leading-relaxed">
                              {activity.description}
                            </p>
                          </div>

                          <div className="px-6 pb-5 flex gap-3">
                            <button
                              onClick={() => toggleReminder(activity.id)}
                              className={`flex-1 px-5 py-3 rounded-full text-sm font-medium transition ${reminders.has(activity.id)
                                  ? "bg-[#1e3a5f] text-white"
                                  : "bg-white text-[#1e3a5f] border border-[#d0d8e6]"
                                }`}
                            >
                              Remind Me
                            </button>
                            <button
                              onClick={() => toggleFavorite(activity.id)}
                              className={`px-6 py-3 rounded-full text-sm font-medium transition flex items-center gap-1.5 ${favorites.has(activity.id)
                                  ? "bg-[#1e3a5f] text-white"
                                  : "bg-white text-[#1e3a5f] border border-[#d0d8e6]"
                                }`}
                            >
                              Favorite
                              <Heart
                                className={`w-4 h-4 ${favorites.has(activity.id) ? "fill-white" : ""
                                  }`}
                              />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
